import axios from "axios";
import { isInReactNativeWebView } from "./utils";

/**
 * True when the shop is running inside the iOS native shell (Expo WebView).
 *
 * The shell injects `window.__nativePlatform` before the page loads. Older
 * app builds only set it once a push token resolves, which never happens if
 * the user declined notifications, so fall back to the WebView's user agent.
 * iPhones report "iPhone"; iPads on iPadOS 13+ report a desktop "Macintosh"
 * UA, so a touch-capable Mac inside the WebView is treated as an iPad.
 */
export const isIosNativeApp = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.__nativePlatform) return window.__nativePlatform === "ios";
  if (!isInReactNativeWebView()) return false;

  const { userAgent, maxTouchPoints } = window.navigator;
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  return /Macintosh/i.test(userAgent) && (maxTouchPoints ?? 0) > 1;
};

/**
 * Tell the API which platform this is, so it can leave out the providers
 * admin has hidden on iOS. The API does the hiding; the shop only reports
 * where it runs. Checked per request because older iOS builds only set
 * `__nativePlatform` once a push token resolves. Only sent to our own API:
 * a custom header would make any third-party host fail its CORS preflight.
 */
export const installPlatformHeader = (): void => {
  const apiUrl = process.env.REACT_APP_API_URL;
  axios.interceptors.request.use((config) => {
    if (apiUrl && config.url?.startsWith(apiUrl) && isIosNativeApp()) {
      config.headers.set("X-Client-Platform", "ios");
    }
    return config;
  });
};
